import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, orderBy } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import CompactReportsFilter from './Filters/CompactReportsFilter';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';
import GSTSummaryReport from './Reports/GSTSummaryReport';
import HsnGstSummaryReport from './Reports/HsnGstSummaryReport';
import TaxPaymentsTab from './Taxes/TaxPaymentsTab';

function getCurrentFinancialYearRange() {
  const now = new Date();
  const year = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  return { start: new Date(year, 3, 1), end: new Date(year + 1, 2, 31, 23, 59, 59, 999) };
}

function getCurrentFinancialYear() {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
}

const Taxes = ({ db, userId, appId, isAuthReady }) => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const [selectedReport, setSelectedReport] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const tab = params.get('tab');
      return tab || 'tax-payments';
    } catch {
      return 'tax-payments';
    }
  });
  const [dateRange, setDateRange] = useState(() => {
    const r = getCurrentFinancialYearRange();
    return { start: new Date(r.start), end: new Date(r.end) };
  });
  const [financialYear, setFinancialYear] = useState(() => getCurrentFinancialYear());
  const [selectedParty, setSelectedParty] = useState('');
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [txnFilter, setTxnFilter] = useState('all'); // Transaction mode filter

  useEffect(() => {
    if (!isAuthReady || !userId || !appId || !db || db.type === 'mock-firestore' || !companyId) {
      setParties([]);
      return;
    }
    const partiesQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'parties'), orderBy('firmName', 'asc'));
    const unsub = onSnapshot(partiesQuery, (snap) => setParties(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [db, userId, appId, isAuthReady, companyId]);

  useEffect(() => {
    if (!isAuthReady || !userId || !appId || !db || db.type === 'mock-firestore' || !companyId) {
      console.log('Taxes: Cannot fetch company details:', { isAuthReady, userId, appId, db: !!db, companyId });
      return;
    }
    console.log('Taxes: Fetching company details for companyId:', companyId);
    const companyDocRef = doc(db, 'companies', companyId);
    const unsub = onSnapshot(companyDocRef, (docSnap) => { 
      if (docSnap.exists()) {
        const data = normalizeCompanyDocSnapshot(docSnap.data() || {}, companyId);
        console.log('Taxes: Company details loaded:', data);
        setCompanyDetails(data);
      } else {
        console.log('Taxes: Company document does not exist for companyId:', companyId);
      }
    }, (error) => {
      console.error('Taxes: Error fetching company details:', error);
    });
    return () => unsub();
  }, [db, userId, appId, isAuthReady, companyId]);

  useEffect(() => {
    const start = new Date(financialYear, 3, 1);
    const end = new Date(financialYear + 1, 2, 31, 23, 59, 59, 999);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) setDateRange({ start, end });
  }, [financialYear]);

  const reportTypes = useMemo(() => {
    // Derive GST type from multiple possible locations in the company document
    const gstTypeRaw = (
      (companyDetails && (
        companyDetails.gstType || 
        companyDetails.gstinType || 
        companyDetails.basicInfo?.gstType ||
        companyDetails.details?.gstinType || 
        companyDetails.settings?.gstinType
      ))
      || ''
    ).toString().trim().toLowerCase();
    const gstType = gstTypeRaw.includes('comp') ? 'composition' : (gstTypeRaw === 'regular' ? 'regular' : '');
    
    // Debug logging for GST type detection
    console.log('Taxes GST Type Detection:', {
      companyDetails: companyDetails ? 'loaded' : 'not loaded',
      gstTypeRaw,
      detectedGstType: gstType,
      companyId,
      availableFields: companyDetails ? Object.keys(companyDetails) : []
    });

    const base = [{ value: 'tax-payments', label: 'Tax Payments' }];

    const addRegular = () => {
      base.push({ value: 'gstr1-regular', label: 'GSTR-1 (Monthly/Quarterly)' });
      base.push({ value: 'gstr3b-regular', label: 'GSTR-3B' });
      base.push({ value: 'hsn-regular', label: 'HSN-wise GST Summary (Regular)' });
    };
    const addComposition = () => {
      base.push({ value: 'gst-summary-composition', label: 'GST Summary (Composition)' });
      base.push({ value: 'hsn-composition', label: 'HSN-wise GST Summary (Composition)' });
    };

    if (!companyDetails || !gstType) {
      // If company details not yet loaded or gst type unknown, show only Tax Payments to avoid confusion
      return base;
    }
    if (gstType === 'regular') addRegular();
    if (gstType === 'composition') addComposition();
    return base;
  }, [companyDetails]);

  useEffect(() => {
    if (!reportTypes.length) return;
    const exists = reportTypes.some(rt => rt.value === selectedReport);
    if (!exists) setSelectedReport(reportTypes[0].value);
  }, [reportTypes]);

  // Keep tab and basic filters in URL
  useEffect(() => {
    if (!hydrated) return;
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (params.get('tab') !== selectedReport) params.set('tab', selectedReport);
    if (dateRange?.start) params.set('start', new Date(dateRange.start).toISOString().slice(0,10));
    if (dateRange?.end) params.set('end', new Date(dateRange.end).toISOString().slice(0,10));
    params.set('fy', String(financialYear));
    if (selectedParty) params.set('party', selectedParty); else params.delete('party');
    const base = window.location.hash.split('?')[0] || '#/taxes';
    const next = `${base}?${params.toString()}`;
    if (window.location.hash !== next) window.location.hash = next;
  }, [hydrated, selectedReport, dateRange, financialYear, selectedParty]);

  // Read URL -> state (supports direct links and navigation)
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const tab = params.get('tab');
    if (tab && tab !== selectedReport) setSelectedReport(tab);
    const s = params.get('start');
    const e = params.get('end');
    const fy = params.get('fy');
    const party = params.get('party') || '';
    if (s && e) {
      const sd = new Date(s); const ed = new Date(e);
      if (!isNaN(sd.getTime()) && !isNaN(ed.getTime())) setDateRange({ start: sd, end: ed });
    }
    if (fy && !isNaN(parseInt(fy,10))) setFinancialYear(parseInt(fy,10));
    setSelectedParty(party);
    if (!hydrated) setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for hash changes to update selected tab and filters during active session
  useEffect(() => {
    const onHashChange = () => {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const tab = params.get('tab');
      if (tab && tab !== selectedReport) setSelectedReport(tab);
      const s = params.get('start');
      const e = params.get('end');
      const fy = params.get('fy');
      const party = params.get('party') || '';
      if (s && e) {
        const sd = new Date(s); const ed = new Date(e);
        if (!isNaN(sd.getTime()) && !isNaN(ed.getTime())) {
          if (
            !dateRange ||
            new Date(dateRange.start).toDateString() !== sd.toDateString() ||
            new Date(dateRange.end).toDateString() !== ed.toDateString()
          ) {
            setDateRange({ start: sd, end: ed });
          }
        }
      }
      const fyNum = parseInt(fy || '', 10);
      if (!isNaN(fyNum) && fyNum !== financialYear) setFinancialYear(fyNum);
      if (party !== selectedParty) setSelectedParty(party);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [selectedReport, dateRange, financialYear, selectedParty]);

  const commonProps = useMemo(() => ({ db, userId, appId, companyId, dateRange, financialYear, selectedParty, parties, loading, setLoading, isAuthReady, companyDetails }), [db, userId, appId, companyId, dateRange, financialYear, selectedParty, parties, loading, isAuthReady, companyDetails]);

  const renderReport = () => {
    // Debug logging
    console.log('Taxes renderReport:', { 
      selectedReport, 
      companyId, 
      companyDetails: companyDetails?.gstinType,
      commonProps: Object.keys(commonProps)
    });
    
    switch (selectedReport) {
      case 'gstr1-regular':
        return <GSTSummaryReport {...commonProps} forcedGstType="regular" reportTypeDefault="gstr1" />;
      case 'gstr3b-regular':
        return <GSTSummaryReport {...commonProps} forcedGstType="regular" reportTypeDefault="gstr3b" />;
      case 'gst-summary-composition':
        return <GSTSummaryReport {...commonProps} forcedGstType="composition" reportTypeDefault="gstr1" />;
      case 'hsn-composition':
        return <HsnGstSummaryReport {...commonProps} forcedGstType="composition" />;
      case 'hsn-regular':
        return <HsnGstSummaryReport {...commonProps} forcedGstType="regular" />;
      case 'tax-payments':
        return <TaxPaymentsTab {...commonProps} />;
      default:
        return <div className="p-6 text-gray-600">Select a report to view.</div>;
    }
  };

  return (
    <div className="p-2 sm:p-6 report-page-root max-w-full">
        <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 max-w-full overflow-hidden" id="taxes-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-0">Taxes Dashboard</h1>
        </div>

        {/* Filters Panel */}
        <div className="space-y-4 mb-4">
          {/* Report Type Selector */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tax Report Type
            </label>
            <AppSelect
              id="tax-report-type-select"
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value)}
              aria-label="Tax report type"
              sheetTitle="Tax report type"
              options={reportTypes.map((report) => ({
                value: report.value,
                label: report.label,
              }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg px-4 py-3 text-sm sm:text-base shadow-sm`}
            />
          </div>

          {/* Compact Filters */}
          <CompactReportsFilter
            parties={parties}
            dateRange={dateRange}
            setDateRange={setDateRange}
            financialYear={financialYear}
            setFinancialYear={setFinancialYear}
            selectedParty={selectedParty}
            setSelectedParty={setSelectedParty}
            txnFilter={txnFilter}
            setTxnFilter={setTxnFilter}
            showTransactionFilter={false}
            onFiltersChange={() => {
              // Trigger any necessary updates when filters change
            }}
          />
        </div>


        
        {/* Report Content */}
        <div id="tax-reports" className="bg-white border border-gray-200 rounded-lg max-w-full overflow-hidden">
          {renderReport()}
        </div>
      </div>
    </div>
  );
};
export default Taxes;